/**
 * Visual Debug Logger for iOS Testing
 * Creates an on-screen overlay to display debug logs
 */

class DebugLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 20;
    this.overlay = null;
    this.enabled = false;
    // Don't init immediately, wait for first log call
  }

  checkEnabled() {
    // Check if debug mode is enabled via URL parameter only
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('debug') === 'true';
  }

  init() {
    if (!this.overlay && this.checkEnabled()) {
      this.enabled = true;
      this.createOverlay();
      // Don't log initialization message, let it stay hidden until first real log
    }
  }

  updateVisibility() {
    if (!this.overlay) return;
    
    // Hide overlay if logs are empty, show if there are logs
    if (this.logs.length === 0) {
      this.overlay.style.display = 'none';
    } else {
      this.overlay.style.display = 'block';
    }
  }

  createOverlay() {
    // Create overlay container
    this.overlay = document.createElement('div');
    this.overlay.id = 'debug-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      max-height: 40vh;
      background: rgba(0, 0, 0, 0.95);
      color: #00ff00;
      font-family: monospace;
      font-size: 11px;
      padding: 10px;
      overflow-y: auto;
      z-index: 999999;
      border-top: 2px solid #00ff00;
      display: none;
    `;

    // Create header with controls
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid #00ff00;
    `;
    
    const title = document.createElement('span');
    title.textContent = '🐛 Debug Logger';
    title.style.fontWeight = 'bold';
    
    const clearBtn = document.createElement('button');
    clearBtn.textContent = 'Clear';
    clearBtn.style.cssText = `
      background: #00ff00;
      color: black;
      border: none;
      padding: 4px 8px;
      border-radius: 3px;
      font-size: 10px;
      cursor: pointer;
    `;
    clearBtn.onclick = () => this.clear();
    
    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = `
      background: #ff0000;
      color: white;
      border: none;
      padding: 4px 8px;
      border-radius: 3px;
      font-size: 10px;
      cursor: pointer;
      margin-left: 5px;
    `;
    closeBtn.onclick = () => this.disable();
    
    header.appendChild(title);
    const btnContainer = document.createElement('div');
    btnContainer.appendChild(clearBtn);
    btnContainer.appendChild(closeBtn);
    header.appendChild(btnContainer);
    
    this.overlay.appendChild(header);
    
    // Create logs container
    this.logsContainer = document.createElement('div');
    this.overlay.appendChild(this.logsContainer);
    
    document.body.appendChild(this.overlay);
  }

  log(message, type = 'info') {
    //console.log("debug log", message)
    // Check if debug should be enabled on each log call
    if (!this.enabled && this.checkEnabled()) {
      this.init();
    }
    
    if (!this.enabled) return;
    
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      timestamp,
      message,
      type
    };
    
    this.logs.push(logEntry);
    
    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    this.render();
    this.updateVisibility();
    
    // Also log to console
    console.log(`[${timestamp}] ${message}`);
  }

  render() {
    if (!this.logsContainer) return;
    
    this.logsContainer.innerHTML = '';
    
    this.logs.forEach(log => {
      const logElement = document.createElement('div');
      logElement.style.cssText = `
        padding: 4px 0;
        border-bottom: 1px solid #333;
      `;
      
      let color = '#00ff00';
      if (log.type === 'error') color = '#ff0000';
      if (log.type === 'warn') color = '#ffaa00';
      if (log.type === 'success') color = '#00ffff';
      
      logElement.innerHTML = `
        <span style="color: #888;">[${log.timestamp}]</span>
        <span style="color: ${color}; margin-left: 8px;">${this.escapeHtml(log.message)}</span>
      `;
      
      this.logsContainer.appendChild(logElement);
    });
    
    // Auto-scroll to bottom
    this.logsContainer.scrollTop = this.logsContainer.scrollHeight;
  }

  clear() {
    this.logs = [];
    this.render();
    this.updateVisibility();
  }

  disable() {
    this.enabled = false;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  enable() {
    this.enabled = true;
    if (!this.overlay) {
      this.createOverlay();
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Create singleton instance
const debugLogger = new DebugLogger();

// Export convenience methods
export const debugLog = (message, type = 'info') => debugLogger.log(message, type);
export const debugError = (message) => debugLogger.log(message, 'error');
export const debugWarn = (message) => debugLogger.log(message, 'warn');
export const debugSuccess = (message) => debugLogger.log(message, 'success');
export const enableDebug = () => debugLogger.enable();
export const disableDebug = () => debugLogger.disable();
export const toggleDebugVisibility = () => debugLogger.toggleVisibility();
export const showDebug = () => debugLogger.show();
export const hideDebug = () => debugLogger.hide();

export default debugLogger;